import React, { useEffect, useRef, useState } from 'react'
import { Button, Divider, Link, Typography } from '@material-ui/core'
import {
  CurrencyList,
  currencyTransform,
  Locale,
} from '../../../../utils/currency-transform'
import { InfoComponentProps } from './info-component.types'
import { ItemCondition, ItemState } from '../../../../models'
import { useInfoComponentStyles } from './info-component.styles'
import { ExpandableText } from './expandable-text'
import { transformToMultiLine } from '../../../../utils/strings'
import { useTranslation } from 'react-i18next'
import { useScreenSize } from '../../../../hooks/use-screen-size'
import Skeleton from '@material-ui/lab/Skeleton'
import Alert from '@material-ui/lab/Alert'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import { DeliveryType } from '../../../../enums'
import { getDateByState, getDateTitle } from '../../../../utils/item-details'
import { AppDialogModerationReject } from '../../../../hooks/app-dialog-moderation-reject/use-app-dialog.component'
import { axiosInstance } from '../../../../api'
import { useNavigate } from 'react-router-dom'

const expandableTextMinimumHeight = 404

export const InfoComponent: React.FC<InfoComponentProps> = (
  props: InfoComponentProps
) => {
  const classes = useInfoComponentStyles()
  const navigate = useNavigate()
  const {
    name,
    description = '',
    currentPrice,
    itemCondition,
    locale = Locale.US,
    currency = CurrencyList.USD,
    isCurrentUserOwner,
    nonprofitName,
    nonprofitUrl,
    listedUtc,
    soldUtc,
    createdUtc,
    donorName,
    state,
    listedItemId,
    city,
    region,
    loading,
    isShippingAvailable,
    deliveryType,
    setInStoreDeliveryType,
  } = props

  const { t } = useTranslation()
  const screenSize = useScreenSize()
  const [isLongText, setIsLongText] = useState<boolean>(false)
  const [isShow, setIsShow] = useState(false)
  const [isModerationActionLoading, setIsModerationActionLoading] =
    useState(false)
  const textHolder = useRef<HTMLDivElement>(null)

  const messageText = () => {
    // eslint-disable-next-line functional/no-let
    let message = null
    if (isCurrentUserOwner) {
      message = t('itemDetails.yourItem')
      state !== ItemState.Active &&
        (message = t('itemDetails.yourUnlistedItem'))
      state === ItemState.Draft && (message = t('itemDetails.yourDraft'))
    }
    return message
  }

  const checkLongText = () => {
    if (!textHolder.current) return
    const textHolderHeight = textHolder.current.offsetHeight
    textHolderHeight > expandableTextMinimumHeight
      ? setIsLongText(true)
      : setIsLongText(false)
  }

  useEffect(checkLongText, [screenSize, description, loading])

  useEffect(() => {
    setInStoreDeliveryType(DeliveryType.Shipping)
  }, [isShippingAvailable])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInStoreDeliveryType((event.target as HTMLInputElement)!.value as string)
  }

  const handleModerationSuccess = () => {
    navigate('/moderation')
  }

  const onClickHandler = (buttonText: string) => {
    if (buttonText === 'Accept') {
      setIsShow(false)
      setIsModerationActionLoading(true)
      const body = {
        RejectReason: '',
        State: 'approved',
      }
      axiosInstance.post(
        `listed-items/${listedItemId}/moderation?listedItemId=${listedItemId}`,
        body
      ).then(handleModerationSuccess).finally(() => setIsModerationActionLoading(false))
    } else if (buttonText === 'Reject') {
      setIsShow(true)
    }
  }

  const renderDetailsRows = () => (
    <div className={classes.detailsGrid}>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.nonprofit')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {nonprofitName ? (
            nonprofitUrl ? (
              <Link
                href={nonprofitUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="body2"
                className={classes.detailValue}
                underline="hover"
              >
                {nonprofitName}
              </Link>
            ) : (
              nonprofitName
            )
          ) : (
            <Skeleton animation="wave" width="75%" />
          )}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.donorName')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {donorName ? donorName : <Skeleton animation="wave" width="70%" />}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.location')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {city ? (
            `${city}, ${region}`
          ) : (
            <Skeleton animation="wave" width="70%" />
          )}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.condition')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {itemCondition != undefined ? (
            itemCondition === ItemCondition.New ? (
              t('itemDetails.new')
            ) : (
              t('itemDetails.used')
            )
          ) : (
            <Skeleton animation="wave" width="70%" />
          )}
        </Typography>
      </div>
    </div>
  )

  return (
    <>
      {isShow && (
        <AppDialogModerationReject
          listedItemId={listedItemId}
          title="Reject item"
          donorName={donorName}
          showCloseButton={true}
          onSubmitSuccess={handleModerationSuccess}
          setIsShow={setIsShow}
        />
      )}

      <div className={classes.infoBlock}>
        <Typography className={classes.listingTitle} variant="h5" component="h1">
          {name ? name : <Skeleton animation="wave" width="70%" />}
        </Typography>
        <div className={classes.listedTimeBlock}>
          {listedUtc ? (
            <>
              <Typography component="span" variant="body2" color="textSecondary">
                {getDateTitle(t, state)}:{' '}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="textPrimary"
                className={classes.listedDateValue}
              >
                {getDateByState({ listedUtc, createdUtc, soldUtc }, state)}
              </Typography>
            </>
          ) : (
            <Skeleton animation="wave" width="50%" />
          )}
        </div>

        {!loading && (
          <>
            {state === ItemState.Active && (
              <div className={classes.ctaSection}>
                <div className={classes.priceRow}>
                  <Typography variant="h6" component="div">
                    {currentPrice ? (
                      currencyTransform(currentPrice, locale, {
                        style: 'currency',
                        currency,
                      })
                    ) : (
                      <Skeleton animation="wave" width={120} />
                    )}
                  </Typography>
                </div>
                <div className={classes.deliveryBlock}>
                  {!messageText() && (
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="deliveryType"
                        name="deliveryType"
                        value={deliveryType}
                        onChange={handleChange}
                      >
                        {isShippingAvailable && (
                          <FormControlLabel
                            value={DeliveryType.Shipping}
                            control={<Radio color="primary" size="small" />}
                            label={
                              <Typography variant="body2" color="textSecondary">
                                Shipping
                              </Typography>
                            }
                          />
                        )}
                      </RadioGroup>
                    </FormControl>
                  )}
                </div>
                <div className={classes.detailsGrid}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    disabled={isModerationActionLoading}
                    onClick={() => onClickHandler('Accept')}
                  >
                    {t('moderation.acceptButton')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    color="primary"
                    disabled={isModerationActionLoading}
                    onClick={() => onClickHandler('Reject')}
                  >
                    {t('moderation.rejectButton')}
                  </Button>
                </div>
                {!!messageText() && (
                  <Typography
                    variant="caption"
                    color="primary"
                    component="span"
                    className={classes.statusMessageBlock}
                  >
                    {messageText()}
                  </Typography>
                )}
              </div>
            )}

            {state === ItemState.Active && (
              <Divider className={classes.divider} />
            )}

            <Typography
              className={classes.sectionTitle}
              variant="subtitle1"
              component="h2"
            >
              {t('itemDetails.detailsHeading')}
            </Typography>
            {renderDetailsRows()}

            <Divider className={classes.divider} />
            {description ? (
              <Typography
                className={classes.sectionTitle}
                variant="subtitle1"
                component="h3"
              >
                {t('itemDetails.description')}
              </Typography>
            ) : (
              <Skeleton animation="wave" width="40%" />
            )}
            <div>
              <Typography
                className={classes.descriptionMeasure}
                ref={textHolder}
                variant="body1"
                color="textSecondary"
                component="p"
              >
                {transformToMultiLine(description)}
              </Typography>
              {!isLongText && (
                <Typography variant="body1" color="textSecondary" component="p">
                  {transformToMultiLine(description)}
                </Typography>
              )}
              {isLongText && <ExpandableText description={description} />}
              {!description && (
                <>
                  <Skeleton animation="wave" width="80%" />
                  <Skeleton animation="wave" width="85%" />
                  <Skeleton animation="wave" width="90%" />
                  <Skeleton animation="wave" width="75%" />
                </>
              )}
            </div>

            {state === ItemState.Sold && (
              <Alert severity="info" className={classes.soldStateAlert}>
                {t('editItemDetailsPage.thisItemWasSold')}
              </Alert>
            )}
          </>
        )}

        {loading && (
          <>
            <div className={classes.ctaSection}>
              <div className={classes.loadingPriceRow}>
                <Skeleton animation="wave" height={30} width="40%" />
              </div>
              <div className={classes.loadingDeliveryBlock}>
                <Skeleton animation="wave" height={24} width={160} />
              </div>
              <Skeleton
                variant="rect"
                height={48}
                style={{ borderRadius: 8, width: '100%' }}
              />
            </div>
            <Divider className={classes.loadingDivider} />
            <Skeleton
              animation="wave"
              height={22}
              width="34%"
              className={classes.loadingSkeletonHeading}
            />
            <div className={classes.loadingDetailsGrid}>
              {[0, 1, 2, 3].map((key) => (
                <div key={key} className={classes.loadingDetailRow}>
                  <Skeleton animation="wave" height={20} width="72%" />
                  <Skeleton animation="wave" height={20} width="88%" />
                </div>
              ))}
            </div>
            <Divider className={classes.loadingDivider} />
            <Skeleton
              animation="wave"
              height={22}
              width="40%"
              className={classes.loadingSkeletonHeading}
            />
            <div className={classes.loadingDescriptionStack}>
              <Skeleton animation="wave" height={22} width="100%" />
              <Skeleton animation="wave" height={22} width="96%" />
              <Skeleton animation="wave" height={22} width="91%" />
              <Skeleton animation="wave" height={22} width="94%" />
              <Skeleton animation="wave" height={22} width="68%" />
              <Skeleton animation="wave" height={22} width="84%" />
            </div>
          </>
        )}
      </div>
    </>
  )
}
